import { useOutletContext } from "react-router-dom";
import { Check } from "lucide-react";
import { NextGenLayoutContext } from "~/components/ManifoldNextGenLayout";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type { ResolvedTheme } from "~/hooks/useNextGenTheme";
import { cn } from "~/lib/utils";
import { SettingsGroup } from "~/pages/manifold-nextgen/components/settings/SettingsGroup";
import { SettingsPage } from "~/pages/manifold-nextgen/components/settings/SettingsPage";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";

// "single" means whichever of light/dark is picked below, not a mode of its own.
type ThemeModeChoice = "single" | "system";

// Colors come from `.mds-swatch-*`, which pins each theme's surfaces so both
// previews stay readable whichever theme is active.
const SWATCH: Record<ResolvedTheme, string> = {
  light: "mds-swatch-light",
  dark: "mds-swatch-dark",
};

const ThemePreview = ({ theme }: { theme: ResolvedTheme }) => (
  <div
    className={cn("flex h-24 gap-1.5 bg-[var(--mds-swatch-surface)] p-2", SWATCH[theme])}
    aria-hidden="true"
  >
    <div className="w-1/4 rounded bg-[var(--mds-swatch-chrome)]" />
    <div className="flex flex-1 flex-col gap-1.5">
      <div className="h-2 w-3/4 rounded-full bg-[var(--mds-swatch-line)]" />
      <div className="h-2 w-full rounded-full bg-[var(--mds-swatch-line)]" />
      <div className="h-2 w-2/3 rounded-full bg-[var(--mds-swatch-line)]" />
    </div>
  </div>
);

const THEMES: { theme: ResolvedTheme; label: string }[] = [
  { theme: "light", label: "Light" },
  { theme: "dark", label: "Dark" },
];

/**
 * The profile's Appearance tab: pick a single theme, or follow the OS. The theme
 * state itself lives on the layout, which hands it down through the profile
 * shell's `Outlet`.
 */
export const ProfileAppearanceTab = () => {
  const { themeMode, resolvedTheme, setThemeMode, onSwitchToClassic } =
    useOutletContext<NextGenLayoutContext>();

  const isSyncing = themeMode === "system";

  // Leaving sync pins the resolved theme, so appearance doesn't jump.
  const onChoiceChange = (choice: ThemeModeChoice) =>
    setThemeMode(choice === "system" ? "system" : resolvedTheme);

  return (
    <SettingsPage title="Appearance" description="Choose how Manifold looks for you.">
      <SettingsGroup>
        <SettingsRow
          title="Theme mode"
          description={
            isSyncing
              ? "Automatically match your system settings."
              : "Use a single theme across the platform."
          }
        >
          <Select
            value={isSyncing ? "system" : "single"}
            onValueChange={(value) => onChoiceChange(value as ThemeModeChoice)}
          >
            <SelectTrigger className="w-56" aria-label="Theme mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single theme</SelectItem>
              <SelectItem value="system">Sync with system</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>

        <div
          className="grid grid-cols-2 gap-4 p-4"
          role={isSyncing ? undefined : "radiogroup"}
          aria-label={isSyncing ? undefined : "Theme"}
        >
          {THEMES.map(({ theme, label }) => {
            const isActive = resolvedTheme === theme;
            const card = (
              <>
                <ThemePreview theme={theme} />
                <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
                  <span className="text-sm font-medium text-foreground">{label}</span>
                  {isSyncing
                    ? isActive && <Badge variant="secondary">Active</Badge>
                    : isActive && <Check className="size-4 text-primary" />}
                </div>
              </>
            );

            // While syncing the themes are status, not choices.
            return isSyncing ? (
              <div
                key={theme}
                role="group"
                aria-label={label}
                className={cn(
                  "overflow-hidden rounded-lg border",
                  isActive ? "border-primary" : "border-border opacity-60"
                )}
              >
                {card}
              </div>
            ) : (
              <button
                key={theme}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => setThemeMode(theme)}
                className={cn(
                  "cursor-pointer overflow-hidden rounded-lg border text-left transition-colors",
                  "focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none",
                  isActive ? "border-primary" : "border-border hover:border-primary/50"
                )}
              >
                {card}
              </button>
            );
          })}
        </div>

        {onSwitchToClassic && (
          <SettingsRow
            title="Classic look"
            description="Switch back to the previous Manifold interface."
          >
            <Button variant="outline" onClick={onSwitchToClassic}>
              Switch to classic look
            </Button>
          </SettingsRow>
        )}
      </SettingsGroup>
    </SettingsPage>
  );
};
